import { z } from 'zod';
import { TextKey } from '@mews-commander/enums';
import { CustomErrorParams, CustomErrorType, createZodFormResolver, useFormControls } from '@mews/framework';
import { useEnablePinMutation } from '../../../queries/useEnablePinMutation';
import { PIN_ENABLE_FORM_ID } from './constants';

export enum PinEnableFormFields {
    Pin = 'pin',
    ConfirmPin = 'confirmPin',
}

type PinEnableFormValues = Record<PinEnableFormFields, string>;

const MIN_PIN_LENGTH = 4;
const MAX_PIN_LENGTH = 6;

const resolver = createZodFormResolver<PinEnableFormValues>(
    z
        .object({
            [PinEnableFormFields.Pin]: z
                .string()
                .min(MIN_PIN_LENGTH)
                .max(MAX_PIN_LENGTH)
                .refine((value) => value && value.length >= MIN_PIN_LENGTH && value.length <= MAX_PIN_LENGTH, {
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.PinLengthError,
                    } satisfies CustomErrorParams,
                })
                .refine((value) => !Number.isNaN(Number(value)), {
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.ValidateNumber,
                    } satisfies CustomErrorParams,
                }),
            [PinEnableFormFields.ConfirmPin]: z.string(),
        })
        .refine((data) => data.pin === data.confirmPin, {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.PinsDontMatch,
            } satisfies CustomErrorParams,
            path: [PinEnableFormFields.ConfirmPin],
        })
);

export const usePinEnableDialogForm = (password: string) => {
    const { mutate, isPending, isSuccess } = useEnablePinMutation();

    const { renderFormContent, submit } = useFormControls<PinEnableFormValues>({
        id: PIN_ENABLE_FORM_ID,
        onSubmit: ({ pin }) => mutate({ pin, password }),
        resolver,
        defaultValues: {
            pin: undefined,
            confirmPin: undefined,
        },
    });

    return { renderFormContent, submit, isLoading: isPending, isSuccess };
};
